import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { AxiosError, AxiosResponse } from 'axios';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { createAxiosInstance } from 'app/agriculture/queries/createAxiosInstance';
import { handleZodParseError } from 'app/agriculture/utils';
import { User } from 'app/agriculture/hooks/users/types';
import { ApiEndpoint } from 'app/agriculture/queries/apiEndpoints';
import { userSchema } from 'app/agriculture/hooks/users/userSchema';
import { ConfigService } from 'app/config/config.service';
import { startupQueries } from 'app/agriculture/contexts/RequiredStartupProviders/queries';

export function useUserDetailsUpdateMutation() {
    const { agricultureToastService } = useContext(ServicesContext);
    const instance = createAxiosInstance({ baseURL: ConfigService.apiUrlRoot });
    const queryClient = useQueryClient();

    type RequestType = Partial<User> & Pick<User, 'id'>;

    return useMutation<User, AxiosError<User, RequestType>, RequestType>({
        mutationFn: async function (payload: RequestType): Promise<User> {
            //console.log(JSON.stringify(payload)); // this is really helpful for nock debugging
            const { data } = await instance.patch<User, AxiosResponse<User>, RequestType>(
                `${ApiEndpoint.users}${payload.id.toString()}/`,
                payload,
            );
            try {
                return userSchema.parse(data) as User;
            } catch (e) {
                handleZodParseError(e);
                throw e;
            }
        },
        onSuccess: async function () {
            await queryClient.invalidateQueries({ queryKey: [ApiEndpoint.users] });
            await queryClient.invalidateQueries({ queryKey: startupQueries.token.queryKey });
        },
        onError: async function (error) {
            console.error('Error updating user', error);
            await agricultureToastService.showToast({
                title: 'User Update Failed',
                subtext: 'Something went wrong',
                type: AgricultureToastType.ERROR,
                variant: 'simple',
            });
        },
    });
}
